import React, { FC, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { KTSVG } from '../../../../../_metronic/helpers'
import { PmsJobRecord, PmsJobAttachmentDto } from '../../core/pms/_models'
import { listJobAttachments, jobAttachmentViewUrl, jobAttachmentDownloadUrl } from '../../core/pms/_requests'
import { resolveRiskLibraryRiskNumber } from '../../../QHSE/core/_requests'
import { FileViewerModal } from '../../../QHSE/components/FileViewerModal'
import { RiskLibraryTaskDetailModal } from './RiskLibraryTaskDetailModal'
import { toast } from 'react-toastify'
import { useAuth } from '../../../auth'

interface Props {
  visible: boolean
  workOrder: PmsJobRecord
  onClose: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const FlagIcon = ({className}: {className?: string}) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden='true'
    className={className}
  >
    <path d='M5 2h2v20H5z' />
    <path d='M7 3h12l-3 4 3 4H7z' />
  </svg>
)

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleString()
}

export const WorkOrderDetailsModal: FC<Props> = ({ visible, workOrder, onClose }) => {
  const { auth } = useAuth()
  const token = auth?.auth?.jwt
  const [attachments, setAttachments] = useState<PmsJobAttachmentDto[]>([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [viewingFile, setViewingFile] = useState<PmsJobAttachmentDto | null>(null)
  const [resolvedLibraryTaskId, setResolvedLibraryTaskId] = useState<number | null>(null)
  const [resolvingRa, setResolvingRa] = useState(false)
  const [riskDetailOpen, setRiskDetailOpen] = useState(false)

  useEffect(() => {
    if (visible && workOrder.id) {
      loadAttachments()
    }
  }, [visible, workOrder.id])

  useEffect(() => {
    if (!visible) {
      setResolvedLibraryTaskId(null)
      setResolvingRa(false)
      setRiskDetailOpen(false)
      return
    }
    const ra = (workOrder.riskAssessmentNumber || '').trim()
    if (!ra) {
      setResolvedLibraryTaskId(null)
      return
    }
    let cancelled = false
    setResolvingRa(true)
    resolveRiskLibraryRiskNumber(ra, null, workOrder.vesselId ?? null)
      .then((opt) => {
        if (!cancelled) setResolvedLibraryTaskId(opt.libraryTaskId ?? null)
      })
      .catch(() => {
        if (!cancelled) setResolvedLibraryTaskId(null)
      })
      .finally(() => {
        if (!cancelled) setResolvingRa(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, workOrder.riskAssessmentNumber, workOrder.vesselId])

  const loadAttachments = async () => {
    if (!workOrder.id) return
    setLoadingAttachments(true)
    try {
      const data = await listJobAttachments(workOrder.id)
      setAttachments(Array.isArray(data) ? data : [])
    } catch (error: any) {
      console.error('Error loading attachments:', error)
      toast.error('Failed to load attachments', {position: 'top-center'})
      setAttachments([])
    } finally {
      setLoadingAttachments(false)
    }
  }

  const filenameFromDisposition = (header?: string | null): string | null => {
    if (!header) return null
    const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header)
    if (star?.[2]) return decodeURIComponent(star[2])
    const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header)
    if (quoted?.[1]) return quoted[1]
    const bare = /filename\s*=\s*([^;]+)/i.exec(header)
    if (bare?.[1]) return bare[1]
    return null
  }

  const handleDownload = async (attachment: PmsJobAttachmentDto) => {
    try {
      const url = jobAttachmentDownloadUrl(attachment)
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) throw new Error(`Download failed: ${res.status}`)
      const cd = res.headers.get('content-disposition')
      const blob = await res.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filenameFromDisposition(cd) || attachment.fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (error: any) {
      console.error('Error downloading attachment:', error)
      toast.error('Failed to download attachment', {position: 'top-center'})
    }
  }

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  if (!visible) return null

  return (
    <>
      <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div
          className='bg-white text-dark rounded shadow-lg d-flex flex-column'
          style={{
            width: '90vw',
            maxWidth: '1100px',
            maxHeight: '90vh',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
            <h5 className='modal-title text-dark m-0'>Work Order Details</h5>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='flex-grow-1 overflow-auto px-4 py-3'>
            {/* Header */}
            <div className='card bg-light mb-4'>
              <div className='card-body p-3'>
                <div className='d-flex flex-wrap gap-2 mb-2'>
                  {workOrder.jobCode && <span className='badge badge-light-primary'>Job Code: {workOrder.jobCode}</span>}
                  {workOrder.jobType && <span className='badge badge-light-info'>{workOrder.jobType}</span>}
                  {workOrder.equipmentName && (
                    <span className='badge badge-light-success'>Equipment: {workOrder.equipmentName}</span>
                  )}
                  {workOrder.subComponentName && (
                    <span className='badge badge-light-warning'>SubComponent: {workOrder.subComponentName}</span>
                  )}
                  {workOrder.vesselName && <span className='badge badge-light'>Vessel: {workOrder.vesselName}</span>}
                  {workOrder.flagged && (
                    <span className='badge bg-danger text-white d-inline-flex align-items-center gap-1'>
                      <FlagIcon className='text-white' />
                      Flagged
                    </span>
                  )}
                </div>
                <div className='fw-semibold'>{workOrder.jobDescription || workOrder.title || '-'}</div>
                {workOrder.jobInstructions && (
                  <div className='text-muted fs-7 mt-1'>Instructions: {workOrder.jobInstructions}</div>
                )}
              </div>
            </div>

            {/* Completion Details */}
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Completion Date</div>
                <div className='fw-bold'>{formatDate(workOrder.actualCompletionDate)}</div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Running Hours at Completion</div>
                <div className='fw-bold'>{workOrder.actualCounterAtCompletion ?? '-'}</div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Performed By</div>
                <div>{workOrder.performerUserName || '-'}</div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Verified By</div>
                <div>{workOrder.verifierUserName || '-'}</div>
              </div>
              <div className='col-12'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Findings</div>
                <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                  {workOrder.findings || '-'}
                </div>
              </div>
              <div className='col-12'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Remarks</div>
                <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                  {workOrder.remarks || '-'}
                </div>
              </div>
              <div className='col-12'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Measured Values</div>
                <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                  {workOrder.measuredValues || '-'}
                </div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Risk Assessment Number</div>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <span className='fw-semibold text-dark'>{workOrder.riskAssessmentNumber || '—'}</span>
                  {resolvingRa && <Spinner animation='border' size='sm' />}
                  {!resolvingRa && resolvedLibraryTaskId != null && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-info'
                      onClick={() => setRiskDetailOpen(true)}
                    >
                      View
                    </button>
                  )}
                </div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Flagged Comment</div>
                <div>{workOrder.flaggedComment || '-'}</div>
              </div>
            </div>

            {/* Attachments */}
            <div className='mb-4'>
              <h6 className='fw-bold mb-3'>Attachments ({attachments.length})</h6>
              {loadingAttachments ? (
                <div className='text-center py-3'>
                  <div className='spinner-border spinner-border-sm' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : attachments.length === 0 ? (
                <div className='text-muted text-center py-3'>No attachments</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle'>
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Size</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((attachment) => (
                        <tr key={attachment.id}>
                          <td>{attachment.fileName}</td>
                          <td>{formatFileSize(attachment.fileSize)}</td>
                          <td>{attachment.uploadedAt ? new Date(attachment.uploadedAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <div className='d-flex gap-1'>
                              <button
                                className='btn btn-sm btn-light'
                                onClick={() => setViewingFile(attachment)}
                                title='View'
                              >
                                <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-2' />
                              </button>
                              <button
                                className='btn btn-sm btn-light'
                                onClick={() => handleDownload(attachment)}
                                title='Download'
                              >
                                <KTSVG path='/media/icons/duotune/arrows/arr076.svg' className='svg-icon-2' />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {viewingFile && (
        <FileViewerModal
          visible={true}
          viewUrl={jobAttachmentViewUrl(viewingFile)}
          downloadUrl={jobAttachmentDownloadUrl(viewingFile)}
          fileName={viewingFile.fileName}
          title={viewingFile.fileName}
          onClose={() => setViewingFile(null)}
        />
      )}

      <RiskLibraryTaskDetailModal
        visible={riskDetailOpen && resolvedLibraryTaskId != null}
        taskId={riskDetailOpen ? resolvedLibraryTaskId : null}
        onClose={() => setRiskDetailOpen(false)}
      />
    </>
  )
}
